import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { fetchWithAuth } from '../../libs/apiUtils'
import Modal from '../Modal'

interface EditMetadataRequest {
  id?: string
  name?: string
  value?: string
  tableId?: string
}

interface EditMetadataModalProps {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  metadata: EditMetadataRequest
  onSuccess: () => void
}

export default function EditMetadataModal({
  isOpen,
  setIsOpen,
  metadata,
  onSuccess,
}: EditMetadataModalProps) {
  const [formData, setFormData] = useState<EditMetadataRequest>(metadata)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (metadata) {
      setFormData({
        name: metadata.name || '',
        value: metadata.value || '',
        id: metadata.id,
        tableId: metadata.tableId,
      })
    }
  }, [metadata])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!formData.name?.trim()) return toast.error('Nama metadata tidak boleh kosong')
    if (!formData.value?.trim()) return toast.error('Nilai metadata tidak boleh kosong')
    if (!formData.id) return toast.error('Tidak ada data yang dipilih')

    try {
      setLoading(true)
      const res = await fetchWithAuth(
        `${process.env.REACT_APP_BACKEND_URL}/api/table-metadata`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        }
      )

      const data = await res.json()

      if (!res.ok || !data.success) {
        toast.error(data[0].message || 'Gagal memperbarui metadata')
        return
      }

      toast.success('Metadata berhasil diperbarui')
      setIsOpen(false)
      onSuccess()
    } catch (err) {
      toast.error('Kesalahan jaringan, periksa koneksi Anda')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
      <h3 className="text-xl font-bold text-neutral-800 mb-4">Edit Metadata</h3>
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="name" className="block text-sm font-semibold text-neutral-600 mb-2">
            Nama Metadata
          </label>
          <input
            type="text"
            id="name"
            name="name"
            className="border border-neutral-300 px-4 py-2 rounded-lg w-full focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all outline-none"
            value={formData.name}
            onChange={handleChange}
            required
            autoFocus
          />
        </div>

        <div className="mb-6">
          <label htmlFor="value" className="block text-sm font-semibold text-neutral-600 mb-2">
            Nilai
          </label>
          <textarea
            id="value"
            name="value"
            rows={3}
            className="border border-neutral-300 px-4 py-2 rounded-lg w-full focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all outline-none"
            value={formData.value}
            onChange={handleChange}
            required
          />
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            className="px-4 py-2 text-neutral-600 font-medium rounded-lg hover:bg-neutral-100 transition-colors"
            onClick={() => setIsOpen(false)}
            disabled={loading}
          >
            Batal
          </button>
          <button
            type="submit"
            className="bg-primary-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-primary-700 active:scale-95 disabled:opacity-50 disabled:active:scale-100 transition-all"
            disabled={loading}
          >
            {loading ? 'Menyimpan...' : 'Simpan Perubahan'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
